import { SectionRowViewModelSizeAndQuantity } from '../../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-row-view-models/section-row-view-model-size-and-quantity';
import { CardStack } from '../menu/section/card-stacks/card-stack';
import { StringUtils } from '../../utils/string-utils';
import { VariantTypeUtils } from '../../utils/variant-type-utils';
import { ProductType } from '../enum/dto/product-type.enum';
import { VariantType } from '../enum/dto/variant-type.enum';
import { exists } from '../../functions/exists';
import { Variant } from '../product/dto/variant';
import { SectionLayoutType } from '../enum/dto/section-layout-type.enum';
import { OpacityLocation } from '../shared/opacity-location.enum';
import { CardBackground } from '../protocols/card-background';
import {
  SectionColumnConfigDataForCannabinoidsAndTerpenesColumn,
  SectionColumnConfigDataValue
} from '../enum/shared/section-column-config-data-value';
import type { Asset } from '../image/dto/asset';
import type { PrintCardColumnViewModel } from '../../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-column-view-models/print-card-column-view-model';
import type { PrintCardMenu } from '../menu/print-card/print-card-menu';
import { memoize } from '../../modules/shared/decorators/memoize';
import { CompoundUtils } from '../../utils/compound-utils';

export class CardData extends SectionRowViewModelSizeAndQuantity implements CardBackground {
  overflowed: boolean = false; // not from API - calculated in generateRowViewModels
  override menu: PrintCardMenu;
  override section: CardStack;

  getLayoutInstructions(): CardStack {
    return this.section;
  }

  /**
   * Only use this for situations when you need bleeding on perforated paper.
   */
  allowPrintCardBackgroundInflatorOverflow(): boolean {
    return false;
  }

  getBackgroundImage(): Asset {
    return this.menu?.hydratedTheme?.themeFeatures?.backgroundMedia ? this.menu?.backgroundImage : null;
  }

  getBackgroundImageAlignment(): string {
    return this.section?.metadata?.imageAlignment;
  }

  getBackgroundImageObjectFit(): string {
    return this.section?.metadata?.objectFit;
  }

  getBackgroundImageWidthAsPercentage(): string {
    return this.section?.metadata?.width;
  }

  getBackgroundAssetWrapperClass(): string {
    return '';
  }

  getBackgroundOpacity(): string {
    const opacityEnabled = this.menu?.getOpacityEnabled();
    const opacityLocationIsBackgroundAsset = this.menu?.getOpacityLocation() === OpacityLocation.BACKGROUND_ASSET;
    return opacityEnabled && opacityLocationIsBackgroundAsset ? this.section?.metadata?.cardOpacity : '0';
  }

  getColorBackgroundAssetBorderRadius(): string {
    return this.menu?.getColorBackgroundAssetBorderRadius();
  }

  getCardColor(): string {
    return this.menu?.hydratedTheme?.themeFeatures?.sectionBodyBackgroundColor
      ? this.section?.metadata?.productsContainerBackgroundColor
      : '';
  }

  getAccentColor(): string {
    return this.menu?.hydratedTheme?.themeFeatures?.sectionHeaderBackgroundColor
      ? this.section?.metadata?.productsInfoBackgroundColor
      : '';
  }

  getHeaderTextColor(): string {
    return this.menu?.hydratedTheme?.themeFeatures?.sectionHeaderTextColor
      ? this.section?.metadata?.sectionHeaderTextColor
      : '';
  }

  getBodyTextColor(): string {
    return this.menu?.hydratedTheme?.themeFeatures?.sectionBodyTextColor
      ? this.section?.metadata?.sectionBodyTextColor
      : '';
  }

  getFormattedUnitSizeFor(variant: Variant): string {
    return variant?.getFormattedUnitSize();
  }

  override getCannabinoid(
    cannabinoid: string,
    displayFormat: SectionColumnConfigDataValue = SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.NumericValue
  ): string {
    const variants = this.getScopedVisibleVariantsElseRowVariants();
    return !variants?.some(variant => variant?.productType === ProductType.Accessories)
      ? StringUtils.removeWhiteSpace(super.getCannabinoid(cannabinoid, displayFormat))
      : null;
  }

  @memoize
  protected override getCannabinoidAsString(
    cannabinoid: string,
    units: string,
    displayFormat: SectionColumnConfigDataValue = SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.NumericValue,
    fixedPoint?: number,
    variant?: Variant
  ): string {
    const parsedCannabinoid = this.getMinRowCannabinoid(cannabinoid, variant);
    switch (true) {
      case displayFormat === SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.PresenceIcon: {
        return parsedCannabinoid > 0 ? '✔' : '-';
      }
      case !Number.isFinite(parsedCannabinoid):
      case parsedCannabinoid < CompoundUtils.smallestAllowableValue: {
        return '-';
      }
      case Number.isFinite(fixedPoint): {
        const fixed = parsedCannabinoid.toFixed(fixedPoint);
        return `${fixed}${this.getCannabinoidFormatedUnitOfMeasureString(units)}`.trim();
      }
      default: {
        const rounded = Math.round((parsedCannabinoid + Number.EPSILON) * 100) / 100;
        return `${rounded}${this.getCannabinoidFormatedUnitOfMeasureString(units)}`.trim();
      }
    }
  }

  @memoize
  protected override getFixedPointRangeStringNoUnits(
    minVal: number,
    maxVal: number,
    displayFormat: SectionColumnConfigDataValue,
    fixedPoint: number | undefined | null,
    displayEntireNumberIfItIsLessThanOne = true
  ): string {
    if (displayFormat === SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.PresenceIcon) {
      return minVal > 0 || maxVal > 0 ? '✔' : '-';
    }
    if (minVal === -1 || maxVal === -1) return '';
    if (minVal === maxVal) {
      if (!displayEntireNumberIfItIsLessThanOne && minVal < CompoundUtils.smallestAllowableValue) {
        return '';
      }
      // ie, don't return 14-14, just return 14,
      return fixedPoint ? minVal.toFixed(fixedPoint) : `${minVal}`;
    }
    return fixedPoint ? minVal.toFixed(fixedPoint) + ' - ' + maxVal.toFixed(fixedPoint) : `${minVal} - ${maxVal}`;
  }

  override getTerpene(
    terpenePascalCased: string,
    displayFormat: SectionColumnConfigDataValue = SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.NumericValue
  ): string {
    const variants = this.getScopedVisibleVariantsElseRowVariants();
    return !variants?.some(variant => variant?.productType === ProductType.Accessories)
      ? StringUtils.removeWhiteSpace(super.getTerpene(terpenePascalCased, displayFormat))
      : null;
  }

  override getThc(): string {
    const variants = this.getScopedVisibleVariantsElseRowVariants();
    return !variants?.some(variant => variant?.productType === ProductType.Accessories)
      ? StringUtils.removeWhiteSpace(super.getThc())
      : null;
  }

  override getCbd(): string {
    const variants = this.getScopedVisibleVariantsElseRowVariants();
    return !variants?.some(variant => variant?.productType === ProductType.Accessories)
      ? StringUtils.removeWhiteSpace(super.getCbd())
      : null;
  }

  override getShutOffLabelForLabelComponent(): boolean {
    return false;
  }

  public allVariantsHaveTheSameUnitSizeOrNull(): string | null {
    return this.rowVariants
      ?.map(v => {
        const size = this.getFormattedUnitSizeFor(v);
        return size === '-' ? null : size;
      })
      ?.uniqueInstance();
  }

  // don't delete - used via sectionRowViewModelBasicMethod
  public getListItemQty(): string {
    const v = this.rowVariants?.firstOrNull();
    switch (true) {
      case ProductType.Seed === v?.productType || VariantType.Seed === v?.variantType: {
        return `${v?.packagedQuantity ?? 1}`;
      }
      case VariantTypeUtils.isPreRollType(v?.variantType):
      case this.section?.isInLineMode() &&
        v?.packagedQuantity > 1 &&
        (v?.productType === ProductType.Beverage || v?.productType === ProductType.Edible): {
        const quantity = v?.packagedQuantity;
        const formattedUnitSize = this.getFormattedUnitSizeFor(v);
        const hasQty = Number.isFinite(quantity);
        const hasSize =
          exists(formattedUnitSize) &&
          formattedUnitSize !== '0' &&
          formattedUnitSize !== '-' &&
          formattedUnitSize !== '--';
        switch (true) {
          case hasQty && hasSize:
            return `${quantity} x ${formattedUnitSize}`;
          case hasQty && !hasSize:
            return `${quantity}`;
          default:
            return '-';
        }
      }
      case VariantTypeUtils.isCapsuleType(v?.variantType): {
        const n = v?.packagedQuantity ?? 0;
        return `${n} cap`
          .pluralizer()
          .addRule({ isPlural: n > 1, useApostrophe: false, word: 'cap' })
          .pluralize();
      }
      case v?.productType === ProductType.Edible && !VariantTypeUtils.isReadyToDrinkBeverageType(v?.variantType): {
        return `${v?.packagedQuantity} ${this.edibleNonReadyToDrinkPackageQuantityWord(v)}`;
      }
      case v?.packagedQuantity > 1: {
        return `${v?.packagedQuantity} ${this.quantityWordForEverythingElse(v)}`;
      }
      default: {
        return super.getSize();
      }
    }
  }

  getStrainNameOrNull(): string {
    const variant = this.rowVariants?.find(v => v?.strain);
    const showStrain =
      variant?.productType === ProductType.Flower ||
      variant?.productType === ProductType.Concentrate ||
      variant?.productType === ProductType.Vape;
    return showStrain ? variant?.strain || null : null;
  }

  override getVariantFromGridColumn(columnVM: PrintCardColumnViewModel): Variant {
    return this.rowVariants?.find(v => {
      const layoutType = columnVM?.sectionLayoutType;
      if (!this.section?.enabledVariantIds?.find(i => i === v?.id)) {
        return false;
      } else if (layoutType === SectionLayoutType.List || layoutType === SectionLayoutType.ChildVariantList) {
        return true;
      } else {
        const lId = this.locationConfig?.locationId;
        const gridNameMatch = v?.getGridNames(layoutType, lId)?.contains(columnVM.columnTitle);
        // Print cards can have variants of the same packQuan/size, so we must also compare against attached variant
        return gridNameMatch && columnVM?.variant?.id === v?.id;
      }
    });
  }

  /**
   * Override to return the average energy score of all visible variants
   */
  getEnergyScore(): number | null {
    const scores = this.getScopedVisibleVariants()
      ?.map(variant => variant?.getEnergyScoreAsNumber())
      ?.filter(score => Number.isFinite(score));
    return scores?.length ? scores.reduce((sum, score) => sum + score, 0) / scores.length : null;
  }

  getAssociatedEffects(): string[] {
    return (
      this.getScopedVisibleVariants()
        ?.flatMap(variant => variant?.effects)
        ?.filterNulls() || []
    );
  }
}
